// File: @/lib/analytics/providers/vercel.ts

/**
 * Vercel Analytics provider adapter — maps canonical AnalyticsEvents to
 * Vercel custom events.
 *
 * Only conversion-style clicks and client errors go to Vercel: its dashboard
 * gives a clean custom-event report next to Web Vitals, while GA4 carries the
 * full engagement picture. Each campaign domain is its own Vercel project, so
 * no site identifier is needed here.
 *
 * VERCEL_EVENT_NAMES doubles as the routing table: an event without an entry
 * is not sent to Vercel (see core.ts).
 */

import { track as vercelTrack } from '@vercel/analytics';

import type { AnalyticsEvent, EventName } from '../types';

type VercelProps = Readonly<Record<string, string>>;

/** Vercel Analytics rejects property values longer than 255 characters. */
const VERCEL_MAX_PROP_VALUE_LENGTH = 255;

export const VERCEL_EVENT_NAMES: Readonly<Partial<Record<EventName, string>>> = {
  'interaction.external_link_click': 'external-link-click',
  'interaction.social_click': 'social-click',
  'interaction.cta_click': 'cta-click',
  'app.client_error': 'client-error',
};

function cap(value: string): string {
  return value.slice(0, VERCEL_MAX_PROP_VALUE_LENGTH);
}

function buildVercelProps(event: AnalyticsEvent): VercelProps {
  if (event.name === 'interaction.external_link_click') {
    return {
      destination_url: cap(event.destination_url),
      link_text: cap(event.link_text),
      placement: event.placement,
    };
  }

  if (event.name === 'interaction.social_click') {
    return { network: event.network, placement: event.placement };
  }

  if (event.name === 'interaction.cta_click') {
    return {
      element_id: event.element_id,
      element_text: cap(event.element_text),
      placement: event.placement,
    };
  }

  if (event.name === 'app.client_error') {
    return {
      error_name: event.error_name,
      error_message: cap(event.error_message),
      ...(event.component_stack !== undefined
        ? { component_stack: cap(event.component_stack) }
        : {}),
    };
  }

  return {};
}

export function sendToVercel(event: AnalyticsEvent): void {
  const vercelEventName = VERCEL_EVENT_NAMES[event.name];
  if (!vercelEventName) return;

  vercelTrack(vercelEventName, buildVercelProps(event));
}
